import { z } from 'zod';
import type { MoneyJson } from '../money/money';
import { moneySchema } from './common.schemas';
import { defineListQuery, listFilter } from './list-query.schemas';
import type { TyreLayoutAxleRole } from './tyre-layout.schemas';

/**
 * The tyre register (PRD → Maintenance → Tyres; gitaumoses4/chukua#177). A tyre is tracked by serial through
 * its life; where it sat and what happened to it is evidence (`tyre_events`, append-only).
 *
 * Two readings the grooming left open: wear is a tread depth in millimetres (a number can be compared and
 * charted, and #178's checks need a threshold to compare it with), and `cost` is the tyre's purchase price only —
 * fitment labour belongs to a work order, not the tyre.
 */

/** `new` is never fitted; `in_stock` is a tyre taken off that can go back on (or be retreaded); `replaced` is worn out and off. */
export const TYRE_STATUSES = ['new', 'in_stock', 'fitted', 'worn', 'replaced', 'scrapped'] as const;
export type TyreStatus = (typeof TYRE_STATUSES)[number];

export const TYRE_EVENT_TYPES = ['registered', 'fitted', 'rotated', 'wear_reading', 'marked_worn', 'repaired', 'retreaded', 'removed', 'scrapped'] as const;
export type TyreEventType = (typeof TYRE_EVENT_TYPES)[number];

/** Why a tyre came off: `reusable` goes back to stock, `worn_out` is replaced. */
export const TYRE_REMOVAL_DISPOSITIONS = ['reusable', 'worn_out'] as const;
export type TyreRemovalDisposition = (typeof TYRE_REMOVAL_DISPOSITIONS)[number];

export const TYRE_VEHICLE_KINDS = ['truck', 'trailer'] as const;
export type TyreVehicleKind = (typeof TYRE_VEHICLE_KINDS)[number];

export const MAX_TREAD_DEPTH_MM = 40;

const text = (max: number) => z.string().trim().min(1).max(max);
const reason = text(500);
const positionCode = z
  .string()
  .trim()
  .min(1)
  .max(20)
  .transform((code) => code.toUpperCase());

/** What the person recording a move may add; the server decides the rest (odometer defaults, future-dated times). */
const moveBase = {
  occurredAt: z.coerce.date().optional(),
  odometerKm: z.number().int().min(0).optional(),
  notes: text(1000).optional(),
};

export const tyreInputSchema = z
  .object({
    serial: text(60).transform((serial) => serial.toUpperCase()),
    brandId: z.uuid(),
    sizeId: z.uuid(),
    pattern: text(80),
    cost: moneySchema.refine((money) => money.amountMinor > 0, { message: 'A tyre must cost more than zero', path: ['amountMinor'] }),
    supplierId: z.uuid().nullable().optional(),
    purchasedOn: z.iso.date(),
  })
  .strict();
export type TyreInput = z.output<typeof tyreInputSchema>;

export const fitTyreInputSchema = z.object({ vehicleKind: z.enum(TYRE_VEHICLE_KINDS), vehicleId: z.uuid(), positionCode, ...moveBase }).strict();
export type FitTyreInput = z.output<typeof fitTyreInputSchema>;

/** `swapWithTyreId` names the tyre in the target position, which takes this tyre's position in the same move. */
export const rotateTyreInputSchema = z.object({ toPositionCode: positionCode, swapWithTyreId: z.uuid().optional(), ...moveBase }).strict();
export type RotateTyreInput = z.output<typeof rotateTyreInputSchema>;

/** `correctsEventId` replaces an earlier wrong reading; it is the only event a correction may point at in v1. */
export const recordWearInputSchema = z
  .object({ treadDepthMm: z.number().min(0).max(MAX_TREAD_DEPTH_MM).multipleOf(0.1), correctsEventId: z.uuid().optional(), ...moveBase })
  .strict();
export type RecordWearInput = z.output<typeof recordWearInputSchema>;

export const tyreNoteInputSchema = z.object(moveBase).strict();
export type TyreNoteInput = z.output<typeof tyreNoteInputSchema>;

export const removeTyreInputSchema = z.object({ reason, disposition: z.enum(TYRE_REMOVAL_DISPOSITIONS), ...moveBase }).strict();
export type RemoveTyreInput = z.output<typeof removeTyreInputSchema>;

export const scrapTyreInputSchema = z.object({ reason, ...moveBase }).strict();
export type ScrapTyreInput = z.output<typeof scrapTyreInputSchema>;

/** The fitted tyre comes off and `withTyreId` goes on in its position, in one step. */
export const replaceTyreInputSchema = z.object({ withTyreId: z.uuid(), reason, disposition: z.enum(TYRE_REMOVAL_DISPOSITIONS), ...moveBase }).strict();
export type ReplaceTyreInput = z.output<typeof replaceTyreInputSchema>;

/** `GET /tyres` — search matches serial and brand. */
export const tyreListQuerySchema = defineListQuery({
  sortFields: ['serial', 'brand', 'size', 'status', 'purchasedOn', 'currentDepthMm'],
  defaultSort: { sort: 'serial', order: 'asc' },
  filters: {
    status: listFilter(z.enum(TYRE_STATUSES)),
    brand: listFilter(z.string().trim().min(1)),
    size: listFilter(z.string().trim().min(1)),
    truckId: listFilter(z.uuid()),
    trailerId: listFilter(z.uuid()),
  },
});
export type TyreListQuery = z.output<typeof tyreListQuerySchema>;

export interface TyreFitmentView {
  id: string;
  vehicleKind: TyreVehicleKind;
  vehicleId: string;
  vehicleRegistration: string | null;
  positionCode: string;
  fittedAt: string;
  fittedOdometerKm: number | null;
  removedAt: string | null;
  removedOdometerKm: number | null;
}

export interface TyreEventView {
  id: string;
  eventType: TyreEventType;
  occurredAt: string;
  recordedAt: string;
  recordedById: string;
  vehicleKind: TyreVehicleKind | null;
  vehicleId: string | null;
  positionCode: string | null;
  toPositionCode: string | null;
  odometerKm: number | null;
  treadDepthMm: number | null;
  reason: string | null;
  notes: string | null;
  correctsEventId: string | null;
}

export interface TyreView {
  id: string;
  serial: string;
  /** The catalogue entry (`tyre_brands`); null only on a tyre not yet backfilled, which the migration rules out. */
  brandId: string | null;
  brand: string;
  sizeId: string | null;
  size: string;
  pattern: string;
  cost: MoneyJson;
  supplierId: string | null;
  purchasedOn: string;
  status: TyreStatus;
  retreadCount: number;
  /** Where it sits now; null when it is not fitted. */
  fitment: TyreFitmentView | null;
  currentDepthMm: number | null;
  /** Null when no span has both ends known (a trailer tyre fitted without an odometer figure). */
  kmOnTyre: number | null;
}

export type TyreListItem = TyreView;

export interface TyreDetail extends TyreView {
  supplierName: string | null;
  fitments: TyreFitmentView[];
  events: TyreEventView[];
}

export interface TyreLayoutSlot {
  positionCode: string;
  name: string;
  axleNumber: number;
  /** A position an admin has since retired from the layout that still holds a tyre. */
  isRetired: boolean;
  tyre: Pick<TyreView, 'id' | 'serial' | 'brand' | 'size' | 'status' | 'currentDepthMm' | 'kmOnTyre'> | null;
}

export interface VehicleTyreLayout {
  vehicleKind: TyreVehicleKind;
  vehicleId: string;
  registration: string;
  /** The colour the vehicle is painted, `#RRGGBB`, for its 3D drawing. */
  color: string;
  layoutCode: string;
  /** The truck class's or trailer type's body style, so a screen draws the right vehicle; null if unknown. */
  bodyStyle: string | null;
  /** Each axle's role, so the drawing places steer, drive and tag axles as the layout does. */
  axleRoles: { axleNumber: number; role: TyreLayoutAxleRole }[];
  slots: TyreLayoutSlot[];
}
